/**
 * Background Studio catalog.
 *
 * A slide background is stored in design.background as one of:
 *   { kind: "preset", presetId, adjust }
 *   { kind: "solid", color, adjust }
 *   { kind: "gradient", angle, stops: [c1, c2, c3?], adjust }
 *   { kind: "image", src, fit, position, scale, adjust }
 *
 * Presets are pure CSS (gradients + inline SVG patterns), so they cost no
 * network requests, render in html2canvas thumbnails, and stay crisp at
 * any presentation size. Cinematic presets add a CSS-animated `fx` layer
 * that is disabled for reduced-motion and low-power devices.
 */

const svgUrl = (svg) => `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

/* ----------------------------- SVG patterns ----------------------------- */

const tissuePattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160" viewBox="0 0 240 160"><g fill="none" stroke="${stroke}" stroke-width="1.2" stroke-linecap="round"><path d="M0 22 C40 6 80 38 120 22 S200 6 240 22"/><path d="M0 52 C40 36 80 68 120 52 S200 36 240 52"/><path d="M0 82 C40 66 80 98 120 82 S200 66 240 82"/><path d="M0 112 C40 96 80 128 120 112 S200 96 240 112"/><path d="M0 142 C40 126 80 158 120 142 S200 126 240 142"/></g><g fill="${stroke}"><ellipse cx="38" cy="37" rx="5" ry="2.4"/><ellipse cx="156" cy="67" rx="5" ry="2.4"/><ellipse cx="92" cy="97" rx="5" ry="2.4"/><ellipse cx="204" cy="127" rx="5" ry="2.4"/></g></svg>`
  );

const cellsPattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><g fill="none" stroke="${stroke}" stroke-width="1.1"><path d="M30 20 L70 14 L96 40 L88 82 L48 92 L20 62 Z"/><path d="M96 40 L140 26 L176 52 L168 96 L126 104 L88 82 Z"/><path d="M48 92 L88 82 L126 104 L120 148 L76 158 L40 132 Z"/><path d="M126 104 L168 96 L196 130 L184 172 L142 180 L120 148 Z"/><path d="M0 150 L40 132 L76 158 L66 200"/></g><g fill="${stroke}"><circle cx="58" cy="52" r="7"/><circle cx="132" cy="64" r="7"/><circle cx="84" cy="122" r="7"/><circle cx="158" cy="140" r="7"/></g></svg>`
  );

const neuronPattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240"><g fill="none" stroke="${stroke}" stroke-width="1" stroke-linecap="round"><path d="M40 50 C90 70 110 30 160 60 S250 120 290 90"/><path d="M160 60 C150 110 120 140 90 190"/><path d="M160 60 C200 90 210 150 250 200"/><path d="M40 50 C30 100 50 150 20 220"/><path d="M290 90 C300 140 280 180 310 230"/><path d="M90 190 C140 200 200 190 250 200"/><path d="M160 60 L180 20"/><path d="M40 50 L10 20"/></g><g fill="${stroke}"><circle cx="40" cy="50" r="5"/><circle cx="160" cy="60" r="6"/><circle cx="290" cy="90" r="5"/><circle cx="90" cy="190" r="4"/><circle cx="250" cy="200" r="5"/></g></svg>`
  );

const dnaPattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="120" viewBox="0 0 320 120"><g fill="none" stroke="${stroke}" stroke-width="1.4"><path d="M0 60 C40 10 80 10 120 60 S200 110 240 60 S300 10 320 35"/><path d="M0 60 C40 110 80 110 120 60 S200 10 240 60 S300 110 320 85"/></g><g stroke="${stroke}" stroke-width="1" opacity="0.8"><line x1="20" y1="38" x2="20" y2="82"/><line x1="40" y1="27" x2="40" y2="93"/><line x1="60" y1="24" x2="60" y2="96"/><line x1="80" y1="27" x2="80" y2="93"/><line x1="100" y1="38" x2="100" y2="82"/><line x1="140" y1="38" x2="140" y2="82"/><line x1="160" y1="27" x2="160" y2="93"/><line x1="180" y1="24" x2="180" y2="96"/><line x1="200" y1="27" x2="200" y2="93"/><line x1="220" y1="38" x2="220" y2="82"/><line x1="260" y1="38" x2="260" y2="82"/><line x1="280" y1="27" x2="280" y2="93"/><line x1="300" y1="24" x2="300" y2="96"/></g></svg>`
  );

const histologyPattern = (fill) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160" viewBox="0 0 160 160"><g fill="${fill}"><circle cx="12" cy="18" r="2.2"/><circle cx="44" cy="9" r="1.4"/><circle cx="70" cy="30" r="3"/><circle cx="108" cy="14" r="1.8"/><circle cx="140" cy="36" r="2.6"/><circle cx="24" cy="58" r="1.6"/><circle cx="56" cy="74" r="2.4"/><circle cx="92" cy="60" r="1.5"/><circle cx="124" cy="82" r="3.2"/><circle cx="150" cy="110" r="1.6"/><circle cx="14" cy="104" r="2.8"/><circle cx="46" cy="124" r="1.6"/><circle cx="80" cy="108" r="2"/><circle cx="104" cy="138" r="2.6"/><circle cx="134" cy="150" r="1.4"/><circle cx="30" cy="150" r="2"/></g><g fill="none" stroke="${fill}" stroke-width="0.8"><ellipse cx="70" cy="30" rx="9" ry="7"/><ellipse cx="124" cy="82" rx="10" ry="8"/><ellipse cx="14" cy="104" rx="9" ry="7"/></g></svg>`
  );

const anatomyPattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720"><g fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round"><path d="M1030 90 L1030 650"/><path d="M1030 150 C960 150 900 190 880 250"/><path d="M1030 150 C1100 150 1160 190 1180 250"/><path d="M1030 205 C950 205 890 250 872 320"/><path d="M1030 205 C1110 205 1170 250 1188 320"/><path d="M1030 260 C955 262 900 310 888 380"/><path d="M1030 260 C1105 262 1160 310 1172 380"/><path d="M1030 315 C965 318 920 360 912 430"/><path d="M1030 315 C1095 318 1140 360 1148 430"/><path d="M1030 370 C978 374 945 410 940 470"/><path d="M1030 370 C1082 374 1115 410 1120 470"/><path d="M960 560 C990 600 1070 600 1100 560"/><path d="M930 520 C980 470 1080 470 1130 520"/></g><g fill="${stroke}"><circle cx="1030" cy="90" r="4"/></g></svg>`
  );

const medicalGridPattern = (minor, major) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><g stroke="${minor}" stroke-width="0.6"><path d="M0 24 H120 M0 48 H120 M0 72 H120 M0 96 H120 M24 0 V120 M48 0 V120 M72 0 V120 M96 0 V120"/></g><g stroke="${major}" stroke-width="1"><path d="M0 0.5 H120 M0.5 0 V120"/></g><g stroke="${major}" stroke-width="1.2"><path d="M60 54 V66 M54 60 H66"/></g></svg>`
  );

const moleculePattern = (stroke) =>
  svgUrl(
    `<svg xmlns="http://www.w3.org/2000/svg" width="260" height="220" viewBox="0 0 260 220"><g fill="none" stroke="${stroke}" stroke-width="1.2" stroke-linejoin="round"><path d="M60 40 L86 55 L86 85 L60 100 L34 85 L34 55 Z"/><path d="M86 55 L112 40 L138 55 L138 85 L112 100 L86 85"/><path d="M138 85 L164 100"/><path d="M190 150 L216 165 L216 195 L190 210 L164 195 L164 165 Z"/><path d="M164 165 L150 140"/><path d="M60 100 L60 130"/></g><g fill="${stroke}"><circle cx="164" cy="100" r="3.5"/><circle cx="150" cy="140" r="3.5"/><circle cx="60" cy="130" r="3.5"/></g></svg>`
  );

/* ------------------------------- Presets -------------------------------- */

export const BACKGROUND_CATEGORIES = [
  { id: "gradients", label: "Modern Gradients" },
  { id: "cinematic", label: "Cinematic" },
  { id: "scientific", label: "Scientific & Medical" },
  { id: "custom", label: "Custom" },
];

export const BACKGROUND_PRESETS = [
  // A. Modern gradients
  { id: "midnight-blue", category: "gradients", label: "Midnight Blue", tone: "dark", css: "linear-gradient(135deg, #0a1428 0%, #12244a 55%, #1b3766 100%)" },
  { id: "deep-purple", category: "gradients", label: "Deep Purple", tone: "dark", css: "linear-gradient(135deg, #160a2a 0%, #33185a 60%, #4c2478 100%)" },
  {
    id: "aurora-gradient",
    category: "gradients",
    label: "Aurora Gradient",
    tone: "dark",
    css: "radial-gradient(ellipse at 18% 22%, rgba(56,189,248,0.32), transparent 52%), radial-gradient(ellipse at 82% 28%, rgba(167,139,250,0.30), transparent 52%), radial-gradient(ellipse at 50% 100%, rgba(52,211,153,0.24), transparent 58%), #0b1424",
  },
  { id: "ocean-blue", category: "gradients", label: "Ocean Blue", tone: "dark", css: "linear-gradient(160deg, #0b5d8f 0%, #0c4a6e 50%, #072c45 100%)" },
  { id: "soft-pastel", category: "gradients", label: "Soft Pastel", tone: "light", css: "linear-gradient(135deg, #fdf2f8 0%, #eef2ff 50%, #ecfeff 100%)" },
  { id: "sunset-glow", category: "gradients", label: "Sunset Glow", tone: "dark", css: "linear-gradient(140deg, #24123f 0%, #5e2160 45%, #a8475f 78%, #d98a5c 100%)" },
  { id: "dark-emerald", category: "gradients", label: "Dark Emerald", tone: "dark", css: "linear-gradient(145deg, #021f19 0%, #053f31 60%, #075442 100%)" },
  { id: "minimal-white", category: "gradients", label: "Minimal White", tone: "light", css: "linear-gradient(180deg, #ffffff 0%, #f3f5f8 100%)" },
  { id: "graphite-black", category: "gradients", label: "Graphite Black", tone: "dark", css: "linear-gradient(160deg, #1c1c1f 0%, #0b0b0c 100%)" },

  // B. Cinematic (animated fx layer)
  { id: "animated-aurora", category: "cinematic", label: "Animated Aurora", tone: "dark", css: "#070f1f", fx: "aurora" },
  { id: "moving-particles", category: "cinematic", label: "Soft Moving Particles", tone: "dark", css: "radial-gradient(ellipse at 50% 0%, #13274a 0%, #08111f 70%)", fx: "particles" },
  { id: "floating-light", category: "cinematic", label: "Floating Light", tone: "dark", css: "linear-gradient(160deg, #0d1830 0%, #0a1222 100%)", fx: "orbs" },
  { id: "mesh-gradient", category: "cinematic", label: "Subtle Mesh Gradient", tone: "dark", css: "#0c1426", fx: "mesh" },
  { id: "atmospheric-glow", category: "cinematic", label: "Atmospheric Glow", tone: "dark", css: "linear-gradient(180deg, #060b16 0%, #0b1730 100%)", fx: "glow" },
  { id: "abstract-shapes", category: "cinematic", label: "Moving Abstract Shapes", tone: "dark", css: "linear-gradient(135deg, #10132a 0%, #1a1440 100%)", fx: "shapes" },
  { id: "depth-layers", category: "cinematic", label: "Depth Layers", tone: "dark", css: "linear-gradient(180deg, #0a1a2e 0%, #050c18 100%)", fx: "depth" },
  { id: "cinematic-spotlight", category: "cinematic", label: "Dark Cinematic Spotlight", tone: "dark", css: "#050608", fx: "spotlight" },

  // C. Scientific & medical (kept very low contrast so content stays primary)
  { id: "tissue", category: "scientific", label: "Microscopic Tissue", tone: "light", css: `${tissuePattern("rgba(190,24,93,0.10)")}, linear-gradient(180deg, #fff7fa 0%, #fbeff4 100%)` },
  { id: "cells", category: "scientific", label: "Cellular Structures", tone: "dark", css: `${cellsPattern("rgba(125,211,252,0.10)")}, linear-gradient(160deg, #0b1b33 0%, #08131f 100%)` },
  { id: "neuron-network", category: "scientific", label: "Neuron Network", tone: "dark", css: `${neuronPattern("rgba(167,139,250,0.16)")}, radial-gradient(ellipse at 30% 30%, #1b1840 0%, #0b0a1c 75%)` },
  { id: "dna-helix", category: "scientific", label: "DNA Helix", tone: "dark", css: `${dnaPattern("rgba(56,189,248,0.13)")}, linear-gradient(160deg, #06263a 0%, #04131f 100%)` },
  { id: "histology-texture", category: "scientific", label: "Histology Texture", tone: "light", css: `${histologyPattern("rgba(124,58,237,0.09)")}, linear-gradient(180deg, #fbf9ff 0%, #f3effc 100%)` },
  { id: "anatomy-silhouette", category: "scientific", label: "Anatomy Silhouettes", tone: "dark", css: `${anatomyPattern("rgba(148,197,255,0.10)")} no-repeat center / cover, linear-gradient(160deg, #0d1b2f 0%, #070f1c 100%)` },
  { id: "medical-grid", category: "scientific", label: "Medical Blue Grid", tone: "light", css: `${medicalGridPattern("rgba(14,116,144,0.07)", "rgba(14,116,144,0.14)")}, linear-gradient(180deg, #f6fbfe 0%, #eaf4fa 100%)` },
  { id: "molecules", category: "scientific", label: "Scientific Illustrations", tone: "light", css: `${moleculePattern("rgba(15,23,42,0.07)")}, #fbfcfd` },
];

export const getBackgroundPreset = (id) => BACKGROUND_PRESETS.find((p) => p.id === id) || null;

const OVERLAY_COLORS = {
  dark: "0, 0, 0",
  light: "255, 255, 255",
};

/** Whether text on top of this background should be light or dark. */
export const backgroundTone = (background) => {
  if (!background) return "light";
  if (background.kind === "preset") return getBackgroundPreset(background.presetId)?.tone || "light";
  const overlay = background.adjust?.overlay;
  if (overlay === "dark" && (background.adjust?.overlayStrength ?? 0) >= 45) return "dark";
  if (overlay === "light" && (background.adjust?.overlayStrength ?? 0) >= 45) return "light";
  const color =
    background.kind === "solid"
      ? background.color
      : background.kind === "gradient"
        ? background.stops?.[0]
        : null;
  if (!color) return "dark";
  return isDarkColor(color) ? "dark" : "light";
};

export const isDarkColor = (hex) => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return false;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Relative luminance (sRGB approximation) is enough to pick text tone.
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 140;
};

/**
 * Translate a stored background into CSS for the backdrop layers.
 * Returns { base: CSSProperties, fx: string|null, overlay: CSSProperties|null }.
 */
export const resolveBackgroundStyle = (background) => {
  if (!background) return null;
  const adjust = background.adjust || {};
  const filters = [];
  if (Number.isFinite(adjust.brightness) && adjust.brightness !== 100) {
    filters.push(`brightness(${adjust.brightness / 100})`);
  }
  if (Number.isFinite(adjust.blur) && adjust.blur > 0) filters.push(`blur(${adjust.blur}px)`);

  const base = {
    position: "absolute",
    inset: adjust.blur > 0 ? -Math.ceil(adjust.blur * 2) : 0,
    opacity: Number.isFinite(adjust.opacity) ? adjust.opacity / 100 : 1,
    filter: filters.length ? filters.join(" ") : undefined,
  };
  let fx = null;

  if (background.kind === "preset") {
    const preset = getBackgroundPreset(background.presetId);
    if (!preset) return null;
    base.background = preset.css;
    fx = preset.fx || null;
  } else if (background.kind === "solid") {
    base.background = background.color || "#ffffff";
  } else if (background.kind === "gradient") {
    const stops = (background.stops || []).filter(Boolean);
    const angle = Number.isFinite(background.angle) ? background.angle : 135;
    base.background =
      stops.length >= 2 ? `linear-gradient(${angle}deg, ${stops.join(", ")})` : stops[0] || "#ffffff";
  } else if (background.kind === "image") {
    if (!background.src) return null;
    const fit = background.fit || "cover";
    const scale = Number.isFinite(background.scale) ? background.scale : 100;
    base.backgroundColor = "#0f172a";
    base.backgroundImage = `url("${background.src}")`;
    base.backgroundRepeat = fit === "tile" ? "repeat" : "no-repeat";
    base.backgroundPosition = background.position || "center";
    base.backgroundSize =
      fit === "tile"
        ? `${Math.round(scale * 2.4)}px auto`
        : fit === "fill"
          ? "100% 100%"
          : scale !== 100
            ? `${scale}% auto`
            : fit;
  } else {
    return null;
  }

  const overlay =
    adjust.overlay && adjust.overlay !== "none" && OVERLAY_COLORS[adjust.overlay]
      ? {
          position: "absolute",
          inset: 0,
          background: `rgba(${OVERLAY_COLORS[adjust.overlay]}, ${(adjust.overlayStrength ?? 35) / 100})`,
        }
      : null;

  return { base, fx, overlay };
};

/** Small CSS background string for swatches / thumbnails. */
export const backgroundSwatchCss = (background) => {
  const resolved = resolveBackgroundStyle(background);
  if (!resolved) return "#ffffff";
  const { base } = resolved;
  if (base.backgroundImage) return `${base.backgroundImage} center / cover no-repeat`;
  return base.background;
};
